import { ActionService } from '@acme/api';
import { toCurrency, useCurrency } from '@acme/domains';
import {
  BreadcrumbItem,
  FileUpload,
  Page,
  renderFieldIfText,
} from '@acme/foundation';
import {
  $agentContract,
  $application,
  $contract,
  applicationOpened,
  applicationReset,
} from '@entities/application';
import { useUnit } from 'effector-react';
import { Button, Card } from 'flowbite-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const MAX_FILE_SIZE_MB = 10;

export const UploadDocumentPage = () => {
  const { id, actionId } = useParams();
  const navigate = useNavigate();
  useCurrency();
  const { application, contract, agentContract, onOpened } = useUnit({
    application: $application,
    contract: $contract,
    agentContract: $agentContract,
    onOpened: applicationOpened,
    onReset: applicationReset,
  });

  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currency = toCurrency(contract?.currency);
  const amountFormatted =
    contract?.amount != null
      ? new Intl.NumberFormat('ru-RU').format(contract.amount)
      : null;

  const agentContractCurrencyName =
    agentContract?.cryptoCurrency &&
    typeof agentContract.cryptoCurrency === 'object' &&
    'name' in agentContract.cryptoCurrency
      ? (agentContract.cryptoCurrency as { name?: string | null }).name
      : null;
  const agentContractCountryName =
    agentContract?.agentPayerCountry &&
    typeof agentContract.agentPayerCountry === 'object' &&
    'name' in agentContract.agentPayerCountry
      ? (agentContract.agentPayerCountry as { name?: string | null }).name
      : null;
  const agentContractWalletNetName =
    agentContract?.agentWalletNet &&
    typeof agentContract.agentWalletNet === 'object' &&
    'name' in agentContract.agentWalletNet
      ? (agentContract.agentWalletNet as { name?: string | null }).name
      : null;

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Главная', onClick: () => navigate('/dashboard') },
    ...(application
      ? [
          {
            label: application.code ?? 'Заявка',
            onClick: () => navigate(`/application/${application.id}`),
          },
          { label: 'Перевод денежных средств' },
        ]
      : [{ label: 'Заявка не найдена' }]),
  ];

  const onFileSelect = useCallback((files: File[]) => {
    setError(null);
    setFile(files[0] ?? null);
  }, []);

  const onFileRemove = useCallback(() => {
    setFile(null);
  }, []);

  const uploadingFiles = useMemo(
    () =>
      file
        ? [
            {
              id: 'selected',
              file,
              status: 'completed' as const,
              progress: 100,
            },
          ]
        : [],
    [file]
  );

  const handleSubmit = useCallback(async () => {
    if (!actionId || !file) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await ActionService.postApiV1ActionUpload({
        id: actionId,
        formData: { files: [file] },
      });
      if (res?.isSuccess) {
        if (id) {
          onOpened({ id });
          navigate(`/application/${id}`);
        }
      } else {
        setError(res?.errorText ?? 'Не удалось отправить документ');
      }
    } catch {
      setError('Не удалось отправить документ');
    } finally {
      setIsSubmitting(false);
    }
  }, [actionId, file, id, navigate, onOpened]);
  return (
    <Page breadcrumbItems={breadcrumbItems}>
      <Card className="divide-y divide-gray-200">
        <div className="flex gap-4 justify-between items-center">
          <h2 className="font-semibold text-xl">Перевод денежных средств</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
          {renderFieldIfText('Контрагент', contract?.counterparty ?? undefined)}
          {renderFieldIfText('Номер заявки', application?.code ?? undefined)}
          {renderFieldIfText('Валюта перевода', currency?.name ?? undefined)}
          {renderFieldIfText('Сумма к оплате', amountFormatted ?? undefined, {
            className: 'font-semibold',
          })}
        </div>
      </Card>

      <Card className="divide-y divide-gray-200 mt-6">
        <h2 className="font-semibold text-xl">Реквизиты для перевода</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4">
          {renderFieldIfText(
            'Комиссия агента',
            agentContract?.agentCommission != null
              ? String(agentContract.agentCommission)
              : undefined
          )}
          {renderFieldIfText(
            'Комиссия уполномоченной организации',
            agentContract?.commissionAuthorizedOrganization != null
              ? String(agentContract.commissionAuthorizedOrganization)
              : undefined
          )}
          {renderFieldIfText(
            'Кошелёк агента',
            agentContract?.agentWallet ?? undefined
          )}
          {renderFieldIfText(
            'Плательщик',
            agentContract?.agentPayerName ?? undefined
          )}
          {renderFieldIfText(
            'Наименование банка плательщика',
            agentContract?.agentPayerBankName ?? undefined
          )}
          {renderFieldIfText(
            'Страна плательщика',
            agentContractCountryName ?? undefined
          )}
          {renderFieldIfText(
            'Сумма в криптовалюте',
            agentContract?.cryptoAmount != null
              ? String(agentContract.cryptoAmount)
              : undefined
          )}
          {renderFieldIfText(
            'Криптовалюта',
            agentContractCurrencyName ?? undefined
          )}
          {renderFieldIfText(
            'Сеть кошелька',
            agentContractWalletNetName ?? undefined
          )}
        </div>
      </Card>

      <Card className="mt-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold text-xl">
            Загрузка платёжного поручения
          </h2>
          <Button
            color="purple"
            onClick={handleSubmit}
            disabled={!file || isSubmitting}
          >
            Отправить
          </Button>
        </div>
        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
        <FileUpload
          accept=".pdf"
          maxSize={MAX_FILE_SIZE_MB}
          multiple={false}
          onFileSelect={onFileSelect}
          onFileRemove={onFileRemove}
          uploadingFiles={uploadingFiles}
        />
      </Card>
    </Page>
  );
};
